import { useCallback, useMemo, useState } from "react";
import { useStorageContext } from "@/context/StorageContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { showToast } from "@int-traderjoes/ui-components-library";
import { getMandatoryFields } from "@/utils/helper";
import { StoragePermissions } from "@/utils/json/FieldPermissions/StoragePermissions";
import { IStorageForm } from "@/types/Storage.types";

interface UseStorageValidationReturn {
  validateStorageForm: () => boolean;
  storageFormErrors: {
    [key: string]: string[];
  };
  setStorageFormErrors: React.Dispatch<React.SetStateAction<{
    [key: string]: string[];
  }>>;
  clearValidationErrors: () => void;
  hasFieldError: (fieldName: string) => boolean;
  clearFieldError: (fieldName: string) => void;
  setFieldError: (fieldName: string) => void;
  isFieldConditionallyMandatory: (fieldName: string) => boolean;
}

export const useStorageValidation = (): UseStorageValidationReturn => {
  const { languageText } = useLanguageContext();
  const { storageForm } = useStorageContext();

  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const toastTexts = languageText?.MESSAGES?.TOAST_MESSAGES;

  const isFieldConditionallyMandatory = useCallback((field: string): boolean => {
    // Implement conditional mandatory logic for Storage if needed
    // For now, all fields are considered mandatory if marked in permissions
    return true;
  }, []);

  const mandatoryFields = useMemo(() => ({
    shelfLifeInformation: getMandatoryFields(StoragePermissions.shelfLifeInformation),
    outerCaseInformation: getMandatoryFields(StoragePermissions.outerCaseInformation),
    lotCodeInformation: getMandatoryFields(StoragePermissions.lotCodeInformation)
  }), []);

  const [storageFormErrors, setStorageFormErrors] = useState<{
    [key: string]: string[];
  }>({});

  const validateStorageForm = useCallback((): boolean => {
    if (!storageForm) {
      showToast({
        type: "error",
        message: toastTexts?.STORAGE_FORM_DATA_NOT_AVAILABLE_ERROR || "Storage form data is not available"
      });
      return false;
    }

    let isFormValid = true;
    const newErrors: { [key: string]: string[] } = {};

    // Validate each section
    Object.entries(mandatoryFields).forEach(([sectionName, fields]) => {
      fields.forEach((field) => {
        const value = (storageForm as any)[field];
        const isEmpty = 
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "") ||
          (typeof value === "number" && value === 0) ||
          (typeof value === "boolean" && value === false);

        if (isEmpty && isFieldConditionallyMandatory(field)) {
          if (!newErrors[sectionName]) newErrors[sectionName] = [];
          newErrors[sectionName].push(field);
          isFormValid = false;
        }
      });
    });

    setStorageFormErrors(newErrors);
    return isFormValid;
  }, [storageForm, mandatoryFields, isFieldConditionallyMandatory, toastTexts?.STORAGE_FORM_DATA_NOT_AVAILABLE_ERROR]);

  const clearValidationErrors = useCallback(() => {
    setStorageFormErrors({});
  }, []);

  const hasFieldError = useCallback((fieldName: string): boolean => {
    return Object.values(storageFormErrors).some(sectionErrors => 
      sectionErrors.includes(fieldName)
    );
  }, [storageFormErrors]);

  const clearFieldError = useCallback((fieldName: string) => {
    setStorageFormErrors(prevErrors => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach(section => {
        if (updatedErrors[section]) {
          updatedErrors[section] = updatedErrors[section].filter(err => err !== fieldName);
        }
      });
      return updatedErrors;
    });
  }, []);

  const setFieldError = useCallback((fieldName: string) => {
    setStorageFormErrors(prevErrors => {
      const updatedErrors = { ...prevErrors };
      // Find which section this field belongs to and add the error
      Object.keys(StoragePermissions).forEach(section => {
        const sectionPermissions = StoragePermissions[section as keyof typeof StoragePermissions];
        if (sectionPermissions && (sectionPermissions as any)[fieldName]) {
          if (!updatedErrors[section]) {
            updatedErrors[section] = [];
          }
          if (!updatedErrors[section].includes(fieldName)) {
            updatedErrors[section].push(fieldName);
          }
        }
      });
      return updatedErrors;
    });
  }, []);

  return {
    validateStorageForm,
    storageFormErrors,
    setStorageFormErrors,
    clearValidationErrors,
    hasFieldError,
    clearFieldError,
    setFieldError,
    isFieldConditionallyMandatory
  };
};
